import React from 'react'
import { type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import {
  FilterGroupsInput,
  type FilterGroupsInputProps,
} from '~/components/features/FilterGroupsInput'
import {
  type ConditionOperation,
  type CustomerAttributeFilterCategory,
} from '~/gql/graphql'
import { compact, type SurveyFilterItem, surveyFilterToText } from '~/utils'

import { BrandSegmentInput } from './components/BrandSegmentInput'
import { CustomerAttributeGroupInput } from './components/CustomerAttributeGroupInput'
import { QuestionInput } from './components/QuestionInput'
import {
  type BrandGroup,
  type CustomerAttributeGroup,
  type InputValues,
  type Question,
} from './utils'

export type SurveyFilterGroupsInputProps = {
  brandGroups: BrandGroup[]
  control: Control<InputValues>
  customerAttributeGroups: CustomerAttributeGroup[]
  'data-testid'?: string
  questions: Question[]
}

export const SurveyFilterGroupsInput = ({
  brandGroups,
  control,
  customerAttributeGroups,
  'data-testid': dataTestId = 'components-features-survey-filter-groups-input',
  questions,
}: SurveyFilterGroupsInputProps) => {
  const { t } = useTranslation()
  const addFilterButtonItems = React.useMemo<
    FilterGroupsInputProps<
      SurveyFilterItem,
      CustomerAttributeFilterCategory
    >['addFilterButtonItems']
  >(
    () =>
      compact([
        0 < questions.length && {
          label: t('select_from_questionnaire_response_2'),
          value: 'Question',
        },
        0 < customerAttributeGroups.length && {
          label: t('select_from_customized_segments_2'),
          value: 'CustomerAttribute',
        },
        0 < brandGroups.length && {
          label: t('select_from_9segs'),
          value: 'BrandSegments',
        },
      ]),
    [brandGroups.length, customerAttributeGroups.length, questions.length, t],
  )

  const categoryToFilter = React.useMemo<
    FilterGroupsInputProps<
      SurveyFilterItem,
      CustomerAttributeFilterCategory
    >['categoryToFilter']
  >(
    () => (category) =>
      match(category)
        .with('Question', (category) => {
          const question = questions[0]
          const answerOption = question.answer_options[0]

          return {
            category,
            question: {
              questionId: question.question_id,
              operation: 'equal' as ConditionOperation,
              answerOptions: [answerOption],
            },
          }
        })
        .with('CustomerAttribute', (category) => {
          const customerAttribute =
            customerAttributeGroups[0].customerAttributes[0]

          return {
            category,
            customerAttribute: {
              operation: 'equal' as ConditionOperation,
              customerAttributeIds: [customerAttribute.id],
            },
          }
        })
        .with('BrandSegments', (category) => {
          const brandGroup = brandGroups[0]
          const brand = brandGroup.brands[0]

          return {
            category,
            brandSegments: {
              brandGroupId: brandGroup.id,
              brandNumber: brand.number,
              segmentNumbers: [],
            },
          }
        })
        .exhaustive(),
    [brandGroups, customerAttributeGroups, questions],
  )

  const filterToText = React.useMemo<
    FilterGroupsInputProps<
      SurveyFilterItem,
      CustomerAttributeFilterCategory
    >['filterToText']
  >(
    () => (filter) =>
      surveyFilterToText({
        brandGroups,
        customerAttributeGroups,
        filter,
        questions,
        t,
      }),
    [brandGroups, customerAttributeGroups, questions, t],
  )

  return (
    <FilterGroupsInput
      addFilterButtonItems={addFilterButtonItems}
      categoryToFilter={categoryToFilter}
      control={control}
      data-testid={dataTestId}
      filterToText={filterToText}
      renderInput={({ category, filterGroupIndex, filterIndex }) =>
        match(category)
          .with('Question', () => (
            <QuestionInput
              control={control}
              filterGroupIndex={filterGroupIndex}
              filterIndex={filterIndex}
              questions={questions}
            />
          ))
          .with('CustomerAttribute', () => (
            <CustomerAttributeGroupInput
              control={control}
              customerAttributeGroups={customerAttributeGroups}
              filterGroupIndex={filterGroupIndex}
              filterIndex={filterIndex}
            />
          ))
          .with('BrandSegments', () => (
            <BrandSegmentInput
              brandGroups={brandGroups}
              control={control}
              filterGroupIndex={filterGroupIndex}
              filterIndex={filterIndex}
            />
          ))
          .exhaustive()
      }
    />
  )
}
